import { Role } from 'auth';
import { get } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { IssuerPortfolioAITrackingModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioAITrackingModal.tsx';
import { CashMovementsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/CashMovementsTab.tsx';
import { PortfolioAssetsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioAssetsTab.tsx';
import { PortfolioBankAccountsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioBankAccountsTab.tsx';
import { PortfolioBrokersTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioBrokersTab.tsx';
import { PortfolioOverviewTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioOverviewTab.tsx';
import { PortfolioSourceEmailsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioSourceEmailsTab.tsx';
import { PortfolioTransactionsTab } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import {
  useIssuerOutdatedAssetsQuery,
  useIssuerPortfolioByIdQuery,
} from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  Button,
  ModalProviderContext,
  PageLayout,
  Skeleton,
  Spinner,
  TabItem,
  Tabs,
} from 'ui';
import { AIIcon, AlertOrangeIcon } from 'ui/icons';

enum PortfolioTabsEnum {
  Overview = 'Overview',
  PortfolioTransactions = 'Portfolio transactions',
  CashMovements = 'Cash movements',
  Assets = 'Assets',
  BankAccounts = 'Bank accounts',
  Brokers = 'Brokers',
  SourceEmails = 'Source emails',
}

const PORTFOLIO_TAB_NAME = 'tab-portfolio';

const IssuerPortfolio = () => {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { openModal } = useContext(ModalProviderContext);
  const paramTab = params.get(PORTFOLIO_TAB_NAME);
  const isValidTab =
    paramTab && Object.values(PortfolioTabsEnum).includes(paramTab as PortfolioTabsEnum);
  const [activeTab, setActiveTab] = useState<PortfolioTabsEnum>(
    isValidTab ? (paramTab as PortfolioTabsEnum) : PortfolioTabsEnum.Overview,
  );

  const {
    data: dataPortfolio,
    isLoading,
    error,
    isError,
  } = useIssuerPortfolioByIdQuery({ portfolioId: (id && +id) || 0 }, !id || isNaN(+id));

  const { data: outdatedAssetsData } = useIssuerOutdatedAssetsQuery(!id || isNaN(+id));

  const outdatedAssetIds = useMemo<Set<number>>(
    () => new Set((outdatedAssetsData?.outdated_assets ?? []).map(a => a.id)),
    [outdatedAssetsData],
  );

  const tabsItems = useMemo<TabItem<PortfolioTabsEnum>[]>(
    () => [
      {
        name: PortfolioTabsEnum.Overview,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioOverviewTab portfolio={dataPortfolio?.portfolio} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.PortfolioTransactions,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioTransactionsTab portfolioId={dataPortfolio?.portfolio?.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.CashMovements,
        container: dataPortfolio?.portfolio?.id ? (
          <CashMovementsTab portfolioId={dataPortfolio.portfolio.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      // { name: PortfolioTabsEnum.BankTransactions, container: <PortfolioBankTransactionsTab /> },
      // {
      //   name: PortfolioTabsEnum.InvestorTransactions,
      //   container: dataPortfolio?.portfolio?.id ? (
      //     <PortfolioInvestorTransactionsTab portfolioId={dataPortfolio?.portfolio?.id} />
      //   ) : (
      //     <div className="flex flex-col w-full h-[250px] justify-center items-center">
      //       <Spinner size="lg" />
      //     </div>
      //   ),
      // },
      {
        name: PortfolioTabsEnum.Assets,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioAssetsTab
            portfolioId={dataPortfolio?.portfolio?.id}
            outdatedAssetIds={outdatedAssetIds}
          />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.BankAccounts,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioBankAccountsTab />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
      {
        name: PortfolioTabsEnum.Brokers,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioBrokersTab portfolioId={dataPortfolio?.portfolio?.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
        // disabled: true,
        // disabledReason: 'We are working on it',
      },
      {
        name: PortfolioTabsEnum.SourceEmails,
        container: dataPortfolio?.portfolio?.id ? (
          <PortfolioSourceEmailsTab portfolioId={dataPortfolio.portfolio.id} />
        ) : (
          <div className="flex flex-col w-full h-[250px] justify-center items-center">
            <Spinner size="lg" />
          </div>
        ),
      },
    ],
    [dataPortfolio?.portfolio, outdatedAssetIds],
  );

  if (get(error, 'response.status') === 404 || isError) {
    params.delete(PORTFOLIO_TAB_NAME);
    // toast.error('This shareholder is not present in this register');
    return (
      <Navigate
        to={{ pathname: issuerDashboardPaths.static.portfolios, search: params.toString() }}
      />
    );
  }

  return (
    <PageLayout
      title="Custody"
      tabs={tabsItems}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      tabNameParam={PORTFOLIO_TAB_NAME}
      contentClassName="px-0 py-2"
      headerBottomContent={
        outdatedAssetsData?.has_outdated ? (
          <Alert
            variant="neutral-2"
            className="mb-0"
            icon={AlertOrangeIcon}
            label="Some assets have not had their prices updated in over 30 days. Please update prices before calculating NAV."
          />
        ) : undefined
      }
      headerAction={
        <div className="flex items-center gap-4 select-none">
          {isLoading ? (
            <>
              <Skeleton width={32} height={32} />
            </>
          ) : (
            <>
              <Button
                variant={'secondary'}
                className="py-1 px-2 min-h-8"
                icon={AIIcon}
                onClick={() => {
                  openModal(IssuerPortfolioAITrackingModal);
                }}
              >
                AI Tracking
              </Button>
            </>
          )}
        </div>
      }
    >
      <Tabs items={tabsItems} activeTab={activeTab} />
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerPortfolio, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
